namespace $.$$ {
	type $shm_surreal_table_result = {
		rows: readonly Record< string, unknown >[]
		count: number
	}

	export class $shm_surreal_table_grid extends $.$shm_surreal_table_grid {
		row_ids() {
			return this.objects().map( ( object, index )=> [ String( object.id ?? index ) ] )
		}

		row_object( row_id: readonly string[] ) {
			const id = row_id[ 0 ]
			return this.objects().find( object => String( object.id ) === id )
				?? this.objects()[ Number( id ) ]
				?? {}
		}

		col_type() { return 'text' }

		col_head_content( col: string ) { return [ col ] }

		cell_content( id: { row: readonly string[], col: string } ) {
			return [ this.value_text( this.row_object( id.row )[ id.col ] ) ]
		}

		value_text( value: unknown ): string {
			if( value === null || value === undefined ) return ''
			if( typeof value === 'string' ) return value
			if( typeof value === 'bigint' ) return value.toString()
			if( typeof value === 'object' ) {
				const text = String( value )
				if( text !== '[object Object]' ) return text
				return JSON.stringify( value )
			}
			return String( value )
		}
	}

	export class $shm_surreal_table extends $.$shm_surreal_table {

		store() {
			return $shm_surreal_reactive_pool.store( this.profile_id() )
		}

		search( next?: string ) {
			const value = super.search( next )
			if( next !== undefined ) this.page_index( 0 )
			return value
		}

		filter_field( next?: string ) {
			const value = super.filter_field( next )
			if( next !== undefined ) this.page_index( 0 )
			return value
		}

		filter_value( next?: string ) {
			const value = super.filter_value( next )
			if( next !== undefined ) this.page_index( 0 )
			return value
		}

		page_size( next?: string ) {
			const value = super.page_size( next )
			if( next !== undefined ) this.page_index( 0 )
			return value
		}

		search_debounce() { return 200 }

		@ $mol_mem
		search_debounced() {
			const search = this.search().trim().toLocaleLowerCase()
			if( search ) this.$.$mol_wait_timeout( this.search_debounce() )
			return search
		}

		@ $mol_mem
		filter_debounced() {
			const value = this.filter_value().trim().toLocaleLowerCase()
			if( value ) this.$.$mol_wait_timeout( this.search_debounce() )
			return value
		}

		filter_fields() {
			return { '': 'Без фильтра', ... Object.fromEntries( this.columns().map( name => [ name, name ] ) ) }
		}

		page_sizes() {
			return { '10': '10', '20': '20', '50': '50', '100': '100' }
		}

		page_limit() {
			const value = Number( this.page_size() )
			return Number.isFinite( value ) ? Math.max( 1, Math.min( 100, value ) ) : 20
		}

		page_count() {
			return Math.max( 1, Math.ceil( this.result().count / this.page_limit() ) )
		}

		page_index( next?: number ) {
			if( next !== undefined ) next = Math.max( 0, Math.floor( next ) )
			return super.page_index( next )
		}

		page_number( next?: number ) {
			if( next !== undefined ) this.page_index( Math.max( 0, Math.floor( next ) - 1 ) )
			return this.page_index() + 1
		}

		Paginator() {
			const paginator = super.Paginator()
			paginator.Backward().enabled = ()=> this.page_index() > 0
			paginator.Forward().enabled = ()=> this.page_index() + 1 < this.page_count()
			return paginator
		}

		where_clause() {
			const conditions: string[] = []
			const bindings: Record< string, unknown > = {}
			const search = this.search_debounced()
			if( search ) {
				const names = this.search_fields().length ? this.search_fields() : this.columns()
				const fields = names.map( $shm_surreal_config_identifier )
				if( fields.length ) {
					conditions.push( `( ${ fields.map( field => `string::lowercase( type::string( ${ field } ) ) CONTAINS $${ '' }search` ).join( ' OR ' ) } )` )
					bindings.search = search
				}
			}
			const filter = this.filter_debounced()
			const filter_field = this.filter_field()
			if( filter && filter_field ) {
				conditions.push( `string::lowercase( type::string( ${ $shm_surreal_config_identifier( filter_field ) } ) ) = $${ '' }filter` )
				bindings.filter = filter
			}
			return { clause: conditions.length ? ` WHERE ${ conditions.join( ' AND ' ) }` : '', bindings }
		}

		query_request() {
			const table = $shm_surreal_config_identifier( this.table_name() )
			const columns = [ 'id', ... this.columns().filter( name => name !== 'id' ) ]
				.map( $shm_surreal_config_identifier ).join( ', ' )
			const where = this.where_clause()
			return {
				statement: [
					`SELECT ${ columns } FROM ${ table }${ where.clause } ORDER BY id LIMIT $${ '' }limit START $${ '' }offset`,
					`SELECT count() AS count FROM ${ table }${ where.clause } GROUP ALL`,
				].join( '; ' ) + ';',
				bindings: {
					... where.bindings,
					limit: this.page_limit(),
					offset: this.page_index() * this.page_limit(),
				},
				tables: [ this.table_name() ],
			} satisfies $shm_surreal_config_query_request
		}

		@ $mol_mem
		result(): $shm_surreal_table_result {
			const response = this.store().query( $shm_surreal_config_query_key( this.query_request() ) )
			const rows = Array.isArray( response[ 0 ] ) ? response[ 0 ] as readonly Record< string, unknown >[] : []
			const counts = Array.isArray( response[ 1 ] ) ? response[ 1 ] as readonly { count?: number }[] : []
			return { rows, count: Number( counts[ 0 ]?.count ?? 0 ) }
		}

		rows() { return this.result().rows }

		count_text() { return `${ this.result().count } записей` }

		status_text() {
			return `${ this.table_name() } · live`
		}

		reload( next?: Event ) {
			if( next ) this.store().invalidate( this.table_name() )
			return next ?? null
		}
	}
}
